/**
 * Seeded mock data for Phase 1 demo (P1-G12 partial_merge included).
 *
 * Every meaningful state is represented at least once so the wizard UI
 * exercises all the visual variants during UAT.
 */

import { Draft, PublishedAgent, WizardInput } from '../services/agent-builder.interface';

const NOW = Math.floor(Date.now() / 1000);

function blankInputs(agentId: string, displayName: string, description: string, department = 'Sales'): WizardInput {
  return {
    identity: {
      agent_id: agentId,
      agent_dir: agentId.toLowerCase(),
      display_name: displayName,
      description,
      department,
      keywords: [],
    },
    shape: {
      has_tools: false,
      tools_via_gateway: false,
      gateway_target: null,
      has_report_artifact: false,
      has_rolling_history: true,
      has_rate_limit: true,
      custom_actions: [],
    },
    prompt: { role: '', instructions: '' },
    tools: [],
    iam_overrides: { extra_statements: [] },
    meta: { renderer: null, capabilities: ['search'], required_inputs: [], suggested_actions: [] },
  };
}

export const SEED_DRAFTS: Draft[] = [
  {
    id: 'draft-1',
    version: 3,
    state: 'draft',
    created_by: 'demo@watchguard.com',
    created_at: NOW - 86_400,
    last_modified: NOW - 3_600,
    inputs: {
      ...blankInputs('STUDIO_INVOICE_HELPER', 'Invoice Helper', 'Looks up customer invoices and reconciliation status.', 'Finance'),
      shape: {
        has_tools: true,
        tools_via_gateway: true,
        gateway_target: 'salesforce-read',
        has_report_artifact: false,
        has_rolling_history: true,
        has_rate_limit: true,
        custom_actions: [],
      },
    },
  },
  {
    id: 'draft-2',
    version: 5,
    state: 'pending_approval',
    created_by: 'developer@watchguard.com',
    created_at: NOW - 172_800,
    last_modified: NOW - 7_200,
    submitted_by: 'developer@watchguard.com',
    submitted_at: NOW - 7_200,
    inputs: blankInputs('STUDIO_PULSE_CHECK', 'Pulse Check', 'Runs a sales-call pulse check against a Salesforce account.', 'Sales'),
    audit_trail: [
      { actor: 'developer@watchguard.com', action: 'created', ts: NOW - 172_800 },
      { actor: 'developer@watchguard.com', action: 'submitted', ts: NOW - 7_200 },
    ],
  },
  {
    id: 'draft-3',
    version: 8,
    state: 'rejected',
    created_by: 'demo@watchguard.com',
    created_at: NOW - 259_200,
    last_modified: NOW - 14_400,
    submitted_by: 'demo@watchguard.com',
    submitted_at: NOW - 21_600,
    approver: 'admin@watchguard.com',
    decision_at: NOW - 14_400,
    reviewer_comments: 'Please scope the IAM permissions tighter — the data table prefix is too broad. See PR-style comments inline.',
    inputs: blankInputs('STUDIO_EXPENSE_REVIEW', 'Expense Review', 'Reviews expense reports for compliance flags.', 'Finance'),
    audit_trail: [
      { actor: 'demo@watchguard.com', action: 'created', ts: NOW - 259_200 },
      { actor: 'demo@watchguard.com', action: 'submitted', ts: NOW - 21_600 },
      { actor: 'admin@watchguard.com', action: 'rejected', ts: NOW - 14_400, comment: 'Please scope the IAM permissions tighter.' },
    ],
  },
];

export const SEED_PUBLISHED: PublishedAgent[] = [
  {
    agent_id: 'STUDIO_DEMOAGENT',
    display_name: 'Demo Agent',
    description: 'A reference agent that demonstrates the wizard scaffolding.',
    department: 'Engineering',
    state: 'live',
    pr1_url: 'https://github.com/WG-AIAgents/wg-agents-studio-runtimes/pull/842',
    pr2_url: 'https://github.com/WG-AIAgents/wg-aiagents-pipelines/pull/156',
    last_activity: NOW - 86_400,
    submitted_by: 'demo@watchguard.com',
    owner: 'demo@watchguard.com',
    approver: 'admin@watchguard.com',
  },
  {
    agent_id: 'STUDIO_NEW_AGENT',
    display_name: 'New Agent Building',
    description: 'Recently approved; CodePipeline is building.',
    department: 'Sales',
    state: 'building',
    pr1_url: 'https://github.com/WG-AIAgents/wg-agents-studio-runtimes/pull/845',
    pr2_url: 'https://github.com/WG-AIAgents/wg-aiagents-pipelines/pull/158',
    last_activity: NOW - 600,
    submitted_by: 'developer@watchguard.com',
    owner: 'developer@watchguard.com',
    approver: 'admin@watchguard.com',
  },
  {
    agent_id: 'STUDIO_BROKEN',
    display_name: 'Broken Agent',
    description: 'Demonstrates the failed-state recovery UX.',
    department: 'Engineering',
    state: 'failed',
    pr1_url: 'https://github.com/WG-AIAgents/wg-agents-studio-runtimes/pull/848',
    last_activity: NOW - 7_200,
    submitted_by: 'demo@watchguard.com',
    owner: 'demo@watchguard.com',
    approver: 'admin@watchguard.com',
    error_context: {
      failed_step: 'open_pr_pipelines_repo',
      error_message: 'GitHub returned 502 Bad Gateway',
      completed_steps: ['pr1_opened'],
      occurred_at: NOW - 7_200,
    },
  },
  {
    agent_id: 'STUDIO_PARTIAL',
    display_name: 'Partial Merge Demo',
    description: 'PR1 merged; reviewers haven\'t looked at the pipelines PR yet (P1-G12).',
    department: 'Sales',
    state: 'partial_merge',
    pr1_url: 'https://github.com/WG-AIAgents/wg-agents-studio-runtimes/pull/850',
    pr2_url: 'https://github.com/WG-AIAgents/wg-aiagents-pipelines/pull/160',
    last_activity: NOW - 3_600,
    submitted_by: 'developer@watchguard.com',
    owner: 'developer@watchguard.com',
    approver: 'admin@watchguard.com',
  },
];

/**
 * Static IAM-block template (P1-G2): mock renders this verbatim with the
 * agent_id substituted in. Real backend's renderer is authoritative; this is
 * an approximate preview for stakeholder demos only.
 */
export const STATIC_IAM_TEMPLATE = `# Approximate — final IAM resolved at publish time.
# {{PASCAL}}AgentCoreExecutionRole
RoleName: !Sub "\${Environment}-{{AGENT_DIR}}-agentcore-execution-role"
AssumeRolePolicyDocument:
  Version: "2012-10-17"
  Statement:
    - Effect: Allow
      Principal: { Service: bedrock-agentcore.amazonaws.com }
      Action: sts:AssumeRole

Policies:
  - PolicyName: {{PASCAL}}AgentCorePolicy
    PolicyDocument:
      Statement:
        - Effect: Allow
          Action: [bedrock:InvokeModel, bedrock:InvokeModelWithResponseStream]
          Resource:
            - arn:aws:bedrock:*:*:inference-profile/us.anthropic.claude-sonnet-4-6
            - arn:aws:bedrock:*:*:inference-profile/us.anthropic.claude-haiku-4-5-20251001-v1:0
        - Effect: Allow
          Action: [dynamodb:GetItem, dynamodb:PutItem, dynamodb:UpdateItem]
          Resource: [!Ref WgAgentsSessionDBTableArn]
        - Effect: Allow
          Action: [sqs:SendMessage]
          Resource: [!Ref WgAiagentsMonitoringQueueArn]
        - Effect: Allow
          Action: [logs:CreateLogGroup, logs:CreateLogStream, logs:PutLogEvents]
          Resource: "*"
{{CONDITIONAL_STATEMENTS}}
`;

/**
 * Canned AI-assist responses for fallback when Bedrock is unreachable (P1-G9).
 */
export const CANNED_AI_PROMPTS: Record<string, { role: string; instructions: string; display_name?: string; description?: string; is_automation?: boolean; schedule_expression?: string; task?: string; delivery_kind?: 'email' | 's3' | 'teams'; abilities?: string[] }> = {
  default: {
    display_name: 'New Agent',
    role: 'You are a helpful WatchGuard AI agent.',
    instructions: 'Respond to user questions concisely. Cite sources where applicable. Refuse off-topic requests.',
    is_automation: false,
    abilities: [],
  },
  sales: {
    display_name: 'Sales Insights Agent',
    role: 'You are a Sales Insights agent.',
    instructions: 'Help WatchGuard sales staff analyse opportunities, accounts, and meetings.\n\nBehavior:\n- Always confirm the account name before searching.\n- Cite the Salesforce record IDs you used.\n- Refuse to disclose data outside the requester\'s territory.',
    is_automation: false,
    abilities: ['query_opportunities', 'query_accounts'],
  },
  sku: {
    display_name: 'SKU Process Agent',
    role: 'You are a WatchGuard SKU Process Expert.',
    instructions: 'Help users look up SKUs, validate attributes, and generate CATSY templates from the in-process knowledge base.',
    is_automation: false,
    abilities: [],
  },
  atlassian: {
    display_name: 'Confluence Q&A Agent',
    role: 'You are a Jira and Confluence search assistant.',
    instructions: 'Search Jira issues and Confluence pages on the user\'s behalf. Always cite issue keys and page URLs.',
    is_automation: false,
    abilities: ['search_confluence', 'search_jira_issues'],
  },
};
